// The sign step: intro (shown while documents build behind it), the
// preparing loader, the embedded PandaDoc surface per family signer —
// owner first, then the insured behind the attestation interstitial —
// the verify gates, and the resting "everything's signed" face.
//
// One discriminated face drives the screen; the server's sign view is
// the only truth (the browser reconstructs any face from a reload). The
// verify polls are a UX affordance — the milestone itself settles
// server-side from the webhook, with or without this tab open.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { ChevronRightIcon } from "@heroicons/react/24/outline";

import Button from "components/Button";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import PandaDocEmbed, {
  type PandaDocEmbedFailureReason,
} from "components/PandaDocEmbed";
import { NetworkError } from "hooks/usePublicAPI";

import { useQuoteFlowAnalytics } from "./analytics";
import {
  createSignSession,
  ensureSignDocuments,
  fetchSignView,
  type ScheduledCall,
  type SignSigner,
  type SignView,
} from "./api";
import ScreenChrome, { NARROW_MAIN_CLASS } from "components/ScreenChrome";
import LoadingScreen from "./LoadingScreen";
import PhoneBand from "./PhoneBand";

const KICKER = "Your documents";

/** The prep loader's stages — honest names for the work actually
 * happening while the packet builds and uploads. */
const PREPARING_STATUSES = [
  "Collecting your application answers…",
  "Preparing the carrier's forms…",
  "Assembling your document set…",
  "Readying the signature fields…",
  "Saving your documents securely…",
] as const;

const VERIFYING_STATUSES = ["Checking your signature…"] as const;

/** The sign view while documents prepare (webhooks land in seconds). */
const PREPARING_POLL_MS = 1500;
/** The gap between a signer finishing in the iframe and the webhook
 * reflecting it on the document record. */
const VERIFY_POLL_MS = 500;
/** Past this, the poll relaxes and the copy levels with the family —
 * the signature was recorded; the server settles without this tab. */
const VERIFY_TIMEOUT_MS = 30_000;
const VERIFY_RELAXED_POLL_MS = 3000;

/** ensure re-fires while the packet shows no progress or after a refusal —
 * bounded per wait, so a persistent upstream failure stops asking. */
const ENSURE_BUDGET = 5;
/** Re-fires from the poll wait at least this long between attempts, so the
 * budget spreads across the wait instead of burning out in seconds. */
const ENSURE_RETRY_GAP_MS = 15_000;
/** A preparing wait past this stops asking the family to watch a spinner:
 * the packet usually lands in seconds, so a minute means something
 * upstream is stuck. */
const PREPARING_STALL_MS = 60_000;
/** The stalled face keeps polling this long from the same start — a late
 * recovery still lands the family in signing. It must outlast doc-service's
 * PandaDoc re-uploads (up to three, each an import plus a page-count check),
 * so the family lands in signing, or on the error those uploads end in,
 * rather than on a give-up. */
const PREPARING_GIVE_UP_MS = 300_000;
/** What the stalled face tells the family. */
const STALLED_NOTICE =
  "Your documents are taking longer than usual to prepare. Once they\u2019re " +
  "ready, we\u2019ll email you a link back to this page so you can pick up " +
  "where you left off.";

type Face =
  | { kind: "loading" }
  | { kind: "intro" }
  | { kind: "preparing"; startedAt: number }
  /** The wait outlived the spinner: the family hears we will follow up
   * and is offered the phone; polling continues until `gaveUp`. */
  | { kind: "stalled"; startedAt: number; gaveUp: boolean }
  | { kind: "signing"; signer: SignSigner; sessionId: string | null }
  | { kind: "verify"; signer: SignSigner; startedAt: number }
  | { kind: "attest"; insured: SignSigner; confirming: boolean }
  | { kind: "done" }
  /** A revisit after every signature landed: Back brought the family
   * here, and the only way is forward — auto-advancing would fight the
   * very Back press that opened it. */
  | { kind: "signed" }
  | { kind: "error"; message: string };

function nextPendingSigner(view: SignView): SignSigner | null {
  return view.signers.find((signer) => !signer.has_completed) ?? null;
}

function signerByRole(
  view: SignView,
  role: SignSigner["role"],
): SignSigner | null {
  return view.signers.find((signer) => signer.role === role) ?? null;
}

export default function SignStep({
  quoteId,
  bookedCall,
  contactPhone,
  onScheduleCall,
  onDone,
}: {
  quoteId: string;
  bookedCall: ScheduledCall | null;
  contactPhone: string | null;
  onScheduleCall: () => void;
  /** Every family signature is in — the flow moves to finalize. */
  onDone: () => void;
}) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  // "Ready" is a level, not an edge — the preparing poll and a reload
  // both observe it, but the family only needs to hear about it once.
  const documentsReadyFiredRef = useRef(false);
  const markDocumentsReady = useCallback(() => {
    if (documentsReadyFiredRef.current) return;
    documentsReadyFiredRef.current = true;
    track("quote_flow_sign_ready");
  }, [track]);
  const [view, setView] = useState<SignView | null>(null);
  const [face, setFace] = useState<Face>({ kind: "loading" });
  const [verifyRelaxed, setVerifyRelaxed] = useState(false);
  const doneRef = useRef(false);
  // The PandaDoc document uuid of the session currently in the embed —
  // set on "loaded", read back at "completed" (which carries no payload
  // fields of its own) and reset per mint so a signer never inherits the
  // previous signer's id.
  const lastDocumentIdRef = useRef<string | null>(null);

  // The one seam past this step: signing is settled — whichever poll or
  // load observed it — and the flow advances to finalize exactly once.
  useEffect(() => {
    if (face.kind !== "done" || doneRef.current) return;
    doneRef.current = true;
    onDone();
  }, [face.kind, onDone]);
  const ensureSpentRef = useRef(0);
  const ensureInFlightRef = useRef(false);
  // A refused ensure is a definitive answer, not a slow one: the face
  // stops spinning at once, and the next tick may retry within the budget.
  const ensureRefusedRef = useRef(false);
  const ensureLastFiredAtRef = useRef(0);
  const budgetReportedRef = useRef(false);
  // Each timed-out milestone reports once PER WAIT — keyed by the wait's
  // start, so a later wait reports its own.
  const stallReportedForRef = useRef<number | null>(null);
  const giveUpReportedForRef = useRef<number | null>(null);
  /** A fresh wait starts with a fresh ensure budget and no refusal held. */
  const resetEnsureState = useCallback(() => {
    ensureSpentRef.current = 0;
    ensureRefusedRef.current = false;
    budgetReportedRef.current = false;
  }, []);

  // ---------------------------------------------------------------
  // Server reads
  // ---------------------------------------------------------------

  // The view poll has no retry budget of its own, so a dead gateway would
  // otherwise report on every tick — one report per visit says the same.
  const refreshFailureReportedRef = useRef(false);
  const refreshView = useCallback(async (): Promise<SignView | null> => {
    try {
      const fresh = await fetchSignView(quoteId);
      setView(fresh);
      return fresh;
    } catch (error) {
      if (!refreshFailureReportedRef.current) {
        refreshFailureReportedRef.current = true;
        reportFailure("fetch_sign_view", error);
      }
      return null;
    }
  }, [quoteId, reportFailure]);

  const fireEnsure = useCallback(() => {
    if (ensureInFlightRef.current) return;
    if (ensureSpentRef.current >= ENSURE_BUDGET) {
      // Out of retries: stop asking. The stalled face already carries the
      // follow-up promise and the phone, so a spinner still up yields to
      // it rather than to a dead end.
      if (!budgetReportedRef.current) {
        budgetReportedRef.current = true;
        reportFailure(
          "ensure_sign_documents",
          new Error("Exceeded the ensure-sign-documents retry budget"),
        );
      }
      setFace((current) =>
        current.kind === "preparing"
          ? { kind: "stalled", startedAt: current.startedAt, gaveUp: false }
          : current,
      );
      return;
    }
    ensureSpentRef.current += 1;
    ensureInFlightRef.current = true;
    ensureLastFiredAtRef.current = Date.now();
    ensureSignDocuments(quoteId)
      .then((fresh) => {
        ensureRefusedRef.current = false;
        setView(fresh);
      })
      .catch((error: unknown) => {
        // Reported once per stall: the first refusal is the news, the
        // bounded retries behind it are not.
        if (!ensureRefusedRef.current) {
          reportFailure("ensure_sign_documents", error);
        }
        ensureRefusedRef.current = true;
        setFace((current) =>
          current.kind === "preparing"
            ? { kind: "stalled", startedAt: current.startedAt, gaveUp: false }
            : current,
        );
      })
      .finally(() => {
        ensureInFlightRef.current = false;
      });
  }, [quoteId, reportFailure]);

  /** Where the wait lands once the family is watching: straight onto the
   * stalled face when the packet already refused behind the intro. */
  const enterWaiting = useCallback(() => {
    const startedAt = Date.now();
    setFace(
      ensureRefusedRef.current
        ? { kind: "stalled", startedAt, gaveUp: false }
        : { kind: "preparing", startedAt },
    );
  }, []);

  // First load: the intro greets every visit except a finished one.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const fresh = await refreshView();
      if (cancelled) return;
      if (!fresh) {
        setFace({
          kind: "error",
          message:
            "We couldn't load your documents — reload the page to try again.",
        });
        return;
      }
      // Already signed on arrival = a revisit (the browser's Back, or a
      // typed address): the standing face, never an auto-advance. Signing
      // that completes DURING this visit still lands on "done" below and
      // moves the flow itself.
      setFace(
        fresh.document.state === "family_signed"
          ? { kind: "signed" }
          : { kind: "intro" },
      );
      if (fresh.document.state === "ready") markDocumentsReady();
      // Documents build behind the intro — kick the packet along the
      // moment we know it isn't ready (covers a reload that landed after
      // payment-confirm's own fire-and-forget was lost).
      if (
        fresh.document.state === "none" ||
        fresh.document.state === "stale" ||
        fresh.document.state === "generating"
      ) {
        fireEnsure();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshView, fireEnsure, markDocumentsReady]);

  // ---------------------------------------------------------------
  // Acts
  // ---------------------------------------------------------------

  const mintSession = useCallback(
    (signer: SignSigner, attested?: boolean) => {
      lastDocumentIdRef.current = null;
      setFace({ kind: "signing", signer, sessionId: null });
      window.scrollTo({ top: 0, behavior: "instant" });
      createSignSession(quoteId, {
        signer_role: signer.role,
        ...(attested ? { attested: true } : {}),
      })
        .then((session) => {
          track("quote_flow_sign_started", { signer_role: signer.role });
          setFace((current) =>
            current.kind === "signing" && current.signer.role === signer.role
              ? { ...current, sessionId: session.session_id }
              : current,
          );
        })
        .catch((error) => {
          // A refusal under 500 means "not signable yet" — expected, and
          // the loader's poll re-enters signing once the packet is;
          // anything else is a real mint failure worth flagging.
          if (error instanceof NetworkError && error.statusCode < 500) {
            track("quote_flow_sign_failed", {
              signer_role: signer.role,
              reason: "not_ready",
              status: error.statusCode,
            });
          } else {
            track("quote_flow_sign_failed", {
              signer_role: signer.role,
              reason: "mint_failed",
              status: error instanceof NetworkError ? error.statusCode : null,
            });
            reportFailure("create_sign_session", error);
          }
          resetEnsureState();
          setFace({ kind: "preparing", startedAt: Date.now() });
        });
    },
    [quoteId, track, reportFailure, resetEnsureState],
  );

  // The insured's session ALWAYS enters through the attestation face —
  // reloads included — so signing them never skips the confirm.
  const enterSigning = useCallback(
    (fresh: SignView) => {
      const pending = nextPendingSigner(fresh);
      if (!pending) {
        setFace({ kind: "done" });
        return;
      }
      const owner = signerByRole(fresh, "owner");
      if (pending.role === "insured" && owner) {
        setFace({ kind: "attest", insured: pending, confirming: false });
        window.scrollTo({ top: 0, behavior: "instant" });
        return;
      }
      mintSession(pending);
    },
    [mintSession],
  );

  const handleIntroContinue = () => {
    if (!view) return;
    if (view.document.state === "ready") {
      enterSigning(view);
      return;
    }
    enterWaiting();
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleAttestConfirm = (insured: SignSigner) => {
    setFace({ kind: "attest", insured, confirming: true });
    mintSession(insured, true);
  };

  // PandaDoc names the document on whichever event carries it — the
  // completion's own payload first, else what document.loaded said.
  const handleSignerCompleted = (
    signer: SignSigner,
    documentId: string | null,
  ) => {
    track("quote_flow_sign_completed", {
      signer_role: signer.role,
      document_id: documentId ?? lastDocumentIdRef.current,
    });
    setVerifyRelaxed(false);
    setFace({ kind: "verify", signer, startedAt: Date.now() });
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  const handleRetryFromError = () => {
    resetEnsureState();
    setFace({ kind: "preparing", startedAt: Date.now() });
    fireEnsure();
  };

  // ---------------------------------------------------------------
  // The polls — preparing watches the packet, verify watches a signer
  // ---------------------------------------------------------------

  // The wait's clock starts when the family starts watching — the build
  // behind the intro does not count. Past the stall the spinner yields to
  // the stalled face; polling continues until the give-up so a recovery
  // still lands them in signing.
  useEffect(() => {
    if (face.kind !== "preparing" && face.kind !== "stalled") return undefined;
    if (face.kind === "stalled" && face.gaveUp) return undefined;
    const { startedAt } = face;
    const waiting = face.kind;
    let cancelled = false;
    const retryEnsure = () => {
      if (Date.now() - ensureLastFiredAtRef.current < ENSURE_RETRY_GAP_MS) {
        return;
      }
      fireEnsure();
    };
    const tick = async () => {
      const fresh = await refreshView();
      if (cancelled || !fresh) return;
      if (fresh.document.state === "family_signed") {
        setFace({ kind: "done" });
        return;
      }
      if (fresh.document.state === "ready") {
        markDocumentsReady();
        enterSigning(fresh);
        return;
      }
      if (fresh.document.state === "error") {
        setFace({
          kind: "error",
          message:
            fresh.document.detail ??
            "Something went wrong preparing the documents.",
        });
        return;
      }
      // Every nudge from the wait is gap-limited: a packet showing no
      // progress and a refused ensure both re-ask, spaced out, within the
      // budget — never on every tick.
      if (
        fresh.document.state === "none" ||
        fresh.document.state === "stale" ||
        ensureRefusedRef.current
      ) {
        retryEnsure();
      }
      const elapsed = Date.now() - startedAt;
      const reportTimedOut = (afterMs: number) =>
        track("quote_flow_request_timed_out", {
          operation: "ensure_sign_documents",
          after_ms: afterMs,
        });
      // Give-up is checked first so a tick that lands past both marks (a
      // throttled background tab) still records both.
      if (elapsed >= PREPARING_GIVE_UP_MS) {
        if (stallReportedForRef.current !== startedAt) {
          stallReportedForRef.current = startedAt;
          reportTimedOut(PREPARING_STALL_MS);
        }
        if (giveUpReportedForRef.current !== startedAt) {
          giveUpReportedForRef.current = startedAt;
          reportTimedOut(PREPARING_GIVE_UP_MS);
        }
        setFace({ kind: "stalled", startedAt, gaveUp: true });
        return;
      }
      if (waiting === "preparing" && elapsed >= PREPARING_STALL_MS) {
        if (stallReportedForRef.current !== startedAt) {
          stallReportedForRef.current = startedAt;
          reportTimedOut(PREPARING_STALL_MS);
        }
        // One more gap-limited nudge at the stall mark, whatever the
        // state reads — a lost fire-and-forget is the usual cause.
        retryEnsure();
        setFace({ kind: "stalled", startedAt, gaveUp: false });
      }
    };
    const timer = window.setInterval(() => void tick(), PREPARING_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [face, refreshView, fireEnsure, enterSigning, markDocumentsReady, track]);

  useEffect(() => {
    if (face.kind !== "verify") return undefined;
    const { signer, startedAt } = face;
    let cancelled = false;
    const tick = async () => {
      const fresh = await refreshView();
      if (cancelled || !fresh) return;
      if (fresh.document.state === "family_signed") {
        setFace({ kind: "done" });
        return;
      }
      const observed = signerByRole(fresh, signer.role);
      if (observed?.has_completed) {
        const pending = nextPendingSigner(fresh);
        if (pending?.role === "insured") {
          setFace({ kind: "attest", insured: pending, confirming: false });
        }
        // Every family signature in but the milestone not yet settled:
        // stay here — the next poll's settlement flips to done.
        return;
      }
      if (!verifyRelaxed && Date.now() - startedAt > VERIFY_TIMEOUT_MS) {
        setVerifyRelaxed(true);
      }
    };
    const timer = window.setInterval(
      () => void tick(),
      verifyRelaxed ? VERIFY_RELAXED_POLL_MS : VERIFY_POLL_MS,
    );
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [face, refreshView, verifyRelaxed]);

  // ---------------------------------------------------------------
  // Faces
  // ---------------------------------------------------------------

  if (face.kind === "loading") {
    return (
      <LoadingScreen
        kicker={KICKER}
        titleA="Checking"
        titleB="your documents"
        statuses={VERIFYING_STATUSES}
      />
    );
  }

  if (face.kind === "error") {
    return (
      <ScreenChrome
        kicker={KICKER}
        titleA="Sign and it's"
        titleB="with our agents"
        blurb=""
      >
        <p className="mt-8 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-base font-medium text-red-800">
          {face.message}
        </p>
        <button
          type="button"
          onClick={handleRetryFromError}
          className={`${CONTINUE_BUTTON_CLASS} mt-6`}
        >
          Try again
        </button>
      </ScreenChrome>
    );
  }

  if (face.kind === "intro" && view) {
    return <IntroFace view={view} onContinue={handleIntroContinue} />;
  }

  if (face.kind === "signed") {
    return (
      <ScreenChrome
        kicker={KICKER}
        titleA="Your documents"
        titleB="have been signed"
        blurb="Every signature is in, exactly as agreed. Nothing here needs another look — continue on to confirm and submit."
      >
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            className={CONTINUE_BUTTON_CLASS}
            onClick={() => {
              track("quote_flow_screen_action", {
                screen: "sign_signed",
                action: "continue",
              });
              onDone();
            }}
          >
            Continue
            <ChevronRightIcon
              className="h-4 w-4"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
        </div>
      </ScreenChrome>
    );
  }

  if (face.kind === "preparing") {
    return (
      <LoadingScreen
        kicker={KICKER}
        titleA="Preparing"
        titleB="your documents"
        statuses={PREPARING_STATUSES}
        showProgressDots
      />
    );
  }

  if (face.kind === "stalled") {
    return (
      <StalledFace
        stillChecking={!face.gaveUp}
        bookedCall={bookedCall}
        contactPhone={contactPhone}
        onSchedule={onScheduleCall}
      />
    );
  }

  if (face.kind === "signing") {
    return (
      <SigningFace
        signer={face.signer}
        sessionId={face.sessionId}
        showSignerLine={(view?.signers.length ?? 0) > 1}
        onLoaded={({ documentId }) => {
          lastDocumentIdRef.current = documentId;
          track("quote_flow_sign_viewed", {
            signer_role: face.signer.role,
            document_id: documentId,
          });
        }}
        onCompleted={({ documentId }) =>
          handleSignerCompleted(face.signer, documentId)
        }
        onError={({ reason, documentId }) => {
          track("quote_flow_sign_failed", {
            signer_role: face.signer.role,
            reason,
            document_id: documentId,
          });
          // The four PandaDoc terminal events are expected business
          // outcomes (a voided/protected/attempted-out document) — only
          // the embed's own exception path is a failure worth reporting.
          if (reason === "embed_error") {
            reportFailure(
              "sign_embed",
              new Error("PandaDoc embed reported an error"),
            );
          }
          setFace({
            kind: "error",
            message:
              "The signing window hit a snag. Try again — your progress on " +
              "signed pages is saved.",
          });
        }}
      />
    );
  }

  if (face.kind === "verify") {
    return (
      <>
        <LoadingScreen
          kicker={KICKER}
          titleA="Verifying"
          titleB="your signature"
          statuses={VERIFYING_STATUSES}
        />
        {verifyRelaxed && (
          <p className="mx-auto -mt-8 max-w-[560px] px-5 pb-10 text-center text-base text-oat-700">
            Your signature was recorded — this is just taking a moment to
            confirm. You can keep waiting or reload the page.
          </p>
        )}
      </>
    );
  }

  if (face.kind === "attest") {
    return (
      <AttestFace
        insured={face.insured}
        attestationText={view?.attestation_text ?? ""}
        confirming={face.confirming}
        onConfirm={() => handleAttestConfirm(face.insured)}
      />
    );
  }

  return <SignedFace />;
}

// -------------------------------------------------------------------
// The faces
// -------------------------------------------------------------------

function IntroFace({
  view,
  onContinue,
}: {
  view: SignView;
  onContinue: () => void;
}) {
  const owner = signerByRole(view, "owner");
  const insured = signerByRole(view, "insured");
  const carrierName = view.carrier?.name ?? "the insurance carrier";
  const twoSigners = owner !== null && insured !== null;

  return (
    <ScreenChrome
      kicker={KICKER}
      titleA="Time to sign"
      titleB="your documents"
      blurb={
        twoSigners
          ? `These are ${carrierName}'s forms — they turn the plan and the answers you gave into the policy itself.`
          : `These are ${carrierName}'s forms — they turn your plan and the answers you gave into the policy itself. Review each page and sign where marked; it usually takes about five minutes.`
      }
    >
      {twoSigners && (
        <ol className="mt-8 max-w-[56ch] space-y-4">
          <IntroStep index={1}>
            <span className="font-semibold text-soil">
              {owner.name ?? "The policy owner"}
            </span>{" "}
            signs as the policy owner.
          </IntroStep>
          <IntroStep index={2}>
            <span className="font-semibold text-soil">
              {insured.name ?? "The insured"}
            </span>{" "}
            signs as the insured — you can complete their signatures on their
            behalf right now if they're with you or you have their permission.
          </IntroStep>
          {view.carrier?.requires_separate_document_packet && (
            <IntroStep index={3}>
              After you finalize with us, {carrierName} sends their own set of
              documents to sign — we'll let you know when to expect them.
            </IntroStep>
          )}
        </ol>
      )}
      {!twoSigners && view.carrier?.requires_separate_document_packet && (
        <p className="mt-6 max-w-[56ch] text-lg leading-relaxed text-oat-700">
          One more thing to expect: after you finalize with us, {carrierName}{" "}
          sends their own set of documents to sign — we'll let you know when
          they're on the way.
        </p>
      )}
      <button
        type="button"
        onClick={onContinue}
        className={`${CONTINUE_BUTTON_CLASS} mt-9`}
      >
        Continue
      </button>
    </ScreenChrome>
  );
}

function IntroStep({
  index,
  children,
}: {
  index: number;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-4 text-lg leading-relaxed text-oat-700">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-evergreen/10 text-sm font-bold text-evergreen">
        {index}
      </span>
      <span>{children}</span>
    </li>
  );
}

function SigningFace({
  signer,
  sessionId,
  showSignerLine,
  onLoaded,
  onCompleted,
  onError,
}: {
  signer: SignSigner;
  sessionId: string | null;
  showSignerLine: boolean;
  onLoaded: (info: { documentId: string | null }) => void;
  onCompleted: (info: { documentId: string | null }) => void;
  onError: (info: {
    reason: PandaDocEmbedFailureReason;
    documentId: string | null;
  }) => void;
}) {
  return (
    <main className={NARROW_MAIN_CLASS}>
      <p className="text-[13px] font-bold uppercase tracking-[0.25em] text-evergreen">
        {KICKER}
      </p>
      <h1 className="mt-2.5 font-serif text-[31px] font-normal leading-[1.15] text-soil sm:text-[39px]">
        Sign and it's{" "}
        <span className="font-sans font-bold text-evergreen">
          with our agents
        </span>
      </h1>
      {showSignerLine && (
        <p className="mt-3 text-lg text-oat-700">
          Signing as{" "}
          <span className="font-semibold text-soil">
            {signer.name ?? signer.role}
          </span>{" "}
          ({signer.role === "owner" ? "policy owner" : "insured"})
        </p>
      )}
      <PandaDocEmbed
        sessionId={sessionId}
        onLoaded={onLoaded}
        onCompleted={onCompleted}
        onError={onError}
        className="mt-6 h-[min(72vh,860px)] overflow-hidden rounded-2xl border border-oat-200 bg-white"
      />
      <p className="mt-4 flex items-center justify-center gap-2 text-sm text-oat-600">
        <LockGlyph />
        Secure signing by PandaDoc
      </p>
    </main>
  );
}

function StalledFace({
  stillChecking,
  bookedCall,
  contactPhone,
  onSchedule,
}: {
  stillChecking: boolean;
  bookedCall: ScheduledCall | null;
  contactPhone: string | null;
  onSchedule: () => void;
}) {
  return (
    <ScreenChrome
      kicker={KICKER}
      titleA="Still preparing"
      titleB="your documents"
      blurb=""
    >
      <div
        role="status"
        className="mt-8 rounded-xl border border-oat-200 bg-oat-50 px-5 py-4 text-base font-medium text-soil"
      >
        <p>{STALLED_NOTICE}</p>
        {stillChecking && (
          <p className="mt-2 font-normal text-oat-700">
            We&rsquo;ll keep checking here for a few minutes &mdash; if they
            finish, this page picks up on its own.
          </p>
        )}
      </div>
      <div className="mt-8">
        <PhoneBand
          bookedCall={bookedCall}
          contactPhone={contactPhone}
          onSchedule={onSchedule}
        />
      </div>
    </ScreenChrome>
  );
}

function LockGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="h-3.5 w-3.5"
      fill="currentColor"
      aria-hidden
    >
      <path d="M8 1a3.5 3.5 0 0 0-3.5 3.5V6H4a1.5 1.5 0 0 0-1.5 1.5v5A1.5 1.5 0 0 0 4 14h8a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 12 6h-.5V4.5A3.5 3.5 0 0 0 8 1Zm2 5H6V4.5a2 2 0 1 1 4 0V6Z" />
    </svg>
  );
}

function AttestFace({
  insured,
  attestationText,
  confirming,
  onConfirm,
}: {
  insured: SignSigner;
  attestationText: string;
  confirming: boolean;
  onConfirm: () => void;
}) {
  return (
    <ScreenChrome
      kicker={KICKER}
      titleA="Now the insured"
      titleB="signs"
      blurb={`The policy owner's signatures are in. ${
        insured.name ?? "The insured"
      } signs their own portion next — you can complete it on their behalf.`}
    >
      <p className="mt-8 max-w-[56ch] rounded-xl border border-oat-200 bg-oat-50 px-5 py-4 text-lg leading-relaxed text-soil">
        {attestationText}
      </p>
      <Button
        variant="continue"
        className="mt-8"
        onClick={onConfirm}
        isLoading={confirming}
      >
        Confirm and Continue
      </Button>
    </ScreenChrome>
  );
}

function SignedFace() {
  return (
    <main className="mx-auto w-full max-w-[560px] flex-1 px-5 pb-16 pt-16 text-center">
      <h1 className="font-serif text-3xl text-soil">Everything's signed</h1>
      <p className="mt-3 text-lg leading-relaxed text-oat-700">
        Your documents are with our licensed agents now. Nothing is charged
        until your policy is finalized — we'll take care of the next step and
        keep you posted.
      </p>
    </main>
  );
}
